/**
 * IndexedDB punch and photo queue (slice 22).
 *
 * Each entry is one tap. client_uuid is chosen once and never changes.
 * tapped_at is the time of the tap, not the time of the sync. A second
 * enqueue with the same id returns the first entry, including its tap time.
 *
 * The photo sits on the same entry. It is sent only when the caller says
 * the photo flag is on. This module does not read the image. The device
 * deletes the JPEG after any success ack that included the photo.
 * held remains only when the post failed, or the photo was never sent.
 */
import { PUNCH_STORE, ROSTER_STORE, openOfflineDb } from './offlineDb';

export const PHOTO_STATUSES = Object.freeze([
  'missing',
  'held',
  'pending_storage',
  'stored',
  'disposed',
]);

const PHOTO_STATUS_SET = new Set(PHOTO_STATUSES);

const OPEN_SHIFT_KEY = 'ota-offline-open-shift';
const ACTIVE_USER_KEY = 'ota-offline-active-user';
const _listeners = new Set();

function stripSecrets(entry) {
  const clean = { ...entry };
  delete clean.pin;
  delete clean.pin_code;
  delete clean.device_secret;
  delete clean.deviceSecret;
  return clean;
}

export function tappedMillis(value) {
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
}

export function rosterRow(row) {
  return {
    employee_id: row?.employee_id || '',
    first_name: row?.first_name || '',
    last_name: row?.last_name || '',
    badge_number: row?.badge_number || '',
  };
}

function notify() {
  _listeners.forEach((fn) => {
    try { fn(); } catch (e) { /* ignore listener errors */ }
  });
}

function newUuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function clone(value) {
  return value == null ? value : JSON.parse(JSON.stringify(value));
}

function idbPunchStore() {
  return {
    async list() {
      const db = await openOfflineDb();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(PUNCH_STORE, 'readonly');
        const req = tx.objectStore(PUNCH_STORE).getAll();
        req.onsuccess = () => resolve(req.result || []);
        req.onerror = () => reject(req.error);
      });
    },
    async get(clientUuid) {
      const db = await openOfflineDb();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(PUNCH_STORE, 'readonly');
        const req = tx.objectStore(PUNCH_STORE).get(clientUuid);
        req.onsuccess = () => resolve(req.result || null);
        req.onerror = () => reject(req.error);
      });
    },
    async put(entry) {
      const db = await openOfflineDb();
      await new Promise((resolve, reject) => {
        const tx = db.transaction(PUNCH_STORE, 'readwrite');
        tx.objectStore(PUNCH_STORE).put(entry);
        tx.oncomplete = resolve;
        tx.onerror = () => reject(tx.error);
      });
    },
    async remove(clientUuid) {
      const db = await openOfflineDb();
      await new Promise((resolve, reject) => {
        const tx = db.transaction(PUNCH_STORE, 'readwrite');
        tx.objectStore(PUNCH_STORE).delete(clientUuid);
        tx.oncomplete = resolve;
        tx.onerror = () => reject(tx.error);
      });
    },
    async listRoster() {
      const db = await openOfflineDb();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(ROSTER_STORE, 'readonly');
        const req = tx.objectStore(ROSTER_STORE).getAll();
        req.onsuccess = () => resolve(req.result || []);
        req.onerror = () => reject(req.error);
      });
    },
    async replaceRoster(rows) {
      const db = await openOfflineDb();
      await new Promise((resolve, reject) => {
        const tx = db.transaction(ROSTER_STORE, 'readwrite');
        const store = tx.objectStore(ROSTER_STORE);
        store.clear();
        rows.forEach((row) => {
          if (row.badge_number) store.put(row);
        });
        tx.oncomplete = resolve;
        tx.onerror = () => reject(tx.error);
      });
    },
  };
}

export function memoryPunchStore() {
  const punches = new Map();
  const roster = new Map();
  return {
    async list() { return [...punches.values()].map(clone); },
    async get(clientUuid) {
      return punches.has(clientUuid) ? clone(punches.get(clientUuid)) : null;
    },
    async put(entry) { punches.set(entry.client_uuid, clone(entry)); },
    async remove(clientUuid) { punches.delete(clientUuid); },
    async listRoster() { return [...roster.values()].map(clone); },
    async replaceRoster(rows) {
      roster.clear();
      rows.forEach((row) => {
        if (row.badge_number) roster.set(row.badge_number, clone(row));
      });
    },
  };
}

let defaultStore = null;
function storeOrDefault(store) {
  if (store) return store;
  if (!defaultStore) defaultStore = idbPunchStore();
  return defaultStore;
}

export function subscribePunchQueue(listener) {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
}

export function rememberOpenShift(clientUuid) {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(OPEN_SHIFT_KEY, clientUuid);
    }
  } catch (e) { /* private mode */ }
}

export function readOpenShift() {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    return sessionStorage.getItem(OPEN_SHIFT_KEY);
  } catch (e) {
    return null;
  }
}

export function clearOpenShift() {
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(OPEN_SHIFT_KEY);
  } catch (e) { /* private mode */ }
}

export function bindActiveUser(userId) {
  try {
    if (typeof sessionStorage === 'undefined') return;
    if (userId == null || userId === '') sessionStorage.removeItem(ACTIVE_USER_KEY);
    else sessionStorage.setItem(ACTIVE_USER_KEY, String(userId));
  } catch (e) { /* private mode */ }
}

export function readActiveUser() {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    return sessionStorage.getItem(ACTIVE_USER_KEY);
  } catch (e) {
    return null;
  }
}

export async function enqueuePunch(tap, store) {
  const db = storeOrDefault(store);
  const clientUuid = tap.client_uuid || tap.clientUuid || newUuid();
  const existing = await db.get(clientUuid);
  if (existing) return existing;
  const tappedAt = tap.tappedAt || tap.tapped_at;
  if (!tappedAt) throw new Error('tapped_at is required');
  const channel = tap.channel || 'phone';
  const userId = tap.userId || tap.user_id || '';
  if (channel !== 'kiosk' && !userId) throw new Error('user_id is required');
  const entry = stripSecrets({
    client_uuid: clientUuid,
    tapped_at: tappedAt,
    action: tap.action,
    channel,
    user_id: userId ? String(userId) : '',
    clock_method: tap.clockMethod || tap.clock_method || 'gps',
    latitude: tap.latitude,
    longitude: tap.longitude,
    accuracy: tap.accuracy ?? null,
    shift_client_uuid: tap.shiftClientUuid || tap.shift_client_uuid || null,
    device_id: tap.deviceId || tap.device_id || '',
    kiosk_id: tap.kioskId || tap.kiosk_id || '',
    employee_id: tap.employeeId || tap.employee_id || '',
    badge_number: tap.badgeNumber || tap.badge_number || '',
    fix_at: tap.fixAt || tap.fix_at || '',
    notes: tap.notes || '',
    photo: tap.photo || null,
    photo_omitted: tap.photoOmitted === true || tap.photo_omitted === true,
    event_code: tap.eventCode || tap.event_code || '',
    assigned_segment: tap.assignedSegment || tap.assigned_segment || '',
    assignment_source: tap.assignmentSource || tap.assignment_source || '',
    label: tap.label || '',
    transport_trip_id: tap.transportTripId || tap.transport_trip_id || null,
    early_reason: tap.earlyReason || tap.early_reason || '',
    early_note: tap.earlyNote || tap.early_note || '',
    open_client_uuid: tap.openClientUuid || tap.open_client_uuid || null,
    location_status: tap.locationStatus || tap.location_status || '',
    status: 'pending',
    photo_status: tap.photo ? 'held' : 'missing',
    attempts: 0,
    last_error: null,
    next_retry_at: null,
  });
  await db.put(entry);
  notify();
  return entry;
}

export function buildSyncBody(entry, { punchPhotoOn = false } = {}) {
  const clientUuid = entry.action === 'out' && entry.shift_client_uuid
    ? entry.shift_client_uuid
    : entry.client_uuid;
  const body = {
    client_uuid: clientUuid,
    action: entry.action,
    tapped_at: entry.tapped_at,
    latitude: entry.latitude,
    longitude: entry.longitude,
    accuracy: entry.accuracy,
    clock_method: entry.clock_method,
    notes: entry.notes || '',
    device_now: new Date().toISOString(),
  };
  if (entry.fix_at) body.fix_at = entry.fix_at;
  if (entry.shift_client_uuid) body.shift_client_uuid = entry.shift_client_uuid;
  if (entry.device_id) body.device_id = entry.device_id;
  if (entry.user_id) body.user_id = entry.user_id;
  if (entry.channel === 'kiosk') {
    body.kiosk_id = entry.kiosk_id;
    body.employee_id = entry.employee_id;
    if (entry.badge_number) body.badge_number = entry.badge_number;
  }
  if (punchPhotoOn && entry.photo && entry.photo_status === 'held') {
    body.photo = {
      content_type: entry.photo.contentType || entry.photo.content_type || 'image/jpeg',
      bytes_base64: entry.photo.bytesBase64 || entry.photo.bytes_base64 || '',
    };
  }
  return body;
}

function nextBackoffMs(attempts) {
  return Math.min(15_000 * 2 ** Math.max(0, attempts - 1), 300_000);
}

function isRetryable(err) {
  const status = err?.response?.status;
  if (!status) return true;
  if (status >= 500) return true;
  return err?.response?.data?.code === 'CLOCK_IN_NOT_SYNCED';
}

function isRejected(entry) {
  return entry.status === 'rejected' || entry.status === 'error';
}

function needsFlush(entry, now, punchPhotoOn) {
  if (isRejected(entry)) return false;
  if (entry.next_retry_at && new Date(entry.next_retry_at).getTime() > now) return false;
  if (entry.attempts >= 5 && entry.status !== 'synced') return false;
  if (entry.status === 'pending' || entry.status === 'syncing') return true;
  return entry.status === 'synced'
    && punchPhotoOn
    && !!entry.photo
    && entry.photo_status === 'held';
}

function belongsToFlush(entry, userId) {
  if (entry.channel === 'kiosk') return true;
  if (!userId || !entry.user_id) return false;
  return String(entry.user_id) === String(userId);
}

function earlierSwitchUnsynced(rows, entry) {
  if (entry.action !== 'out' || entry.channel === 'kiosk') return false;
  const outAt = tappedMillis(entry.tapped_at);
  return rows.some((row) => (
    row.action === 'switch'
    && row.channel !== 'kiosk'
    && String(row.user_id || '') === String(entry.user_id || '')
    && row.status !== 'synced'
    && row.status !== 'rejected'
    && tappedMillis(row.tapped_at) <= outAt
  ));
}

function buildSwitchSyncBody(entry, { punchPhotoOn = false } = {}) {
  const body = {
    action: 'switch',
    event_code: entry.event_code || '',
    assigned_segment: entry.assigned_segment || '',
    label: entry.label || '',
    early_reason: entry.early_reason || '',
    early_note: entry.early_note || '',
  };
  if (entry.assignment_source) body.assignment_source = entry.assignment_source;
  if (entry.transport_trip_id) body.transport_trip_id = entry.transport_trip_id;
  if (entry.open_client_uuid) body.client_uuid = entry.open_client_uuid;
  if (entry.tapped_at) body.client_tapped_at = entry.tapped_at;
  body.device_now = new Date().toISOString();
  if (entry.latitude != null && entry.longitude != null) {
    body.latitude = entry.latitude;
    body.longitude = entry.longitude;
  }
  if (entry.accuracy != null && entry.accuracy !== '') body.accuracy = entry.accuracy;
  if (entry.fix_at) body.fix_at = entry.fix_at;
  if (entry.location_status) body.location_status = entry.location_status;
  if (punchPhotoOn && entry.photo && entry.photo_status === 'held') {
    body.photo_jpeg = entry.photo.bytesBase64 || entry.photo.bytes_base64 || '';
  } else if (entry.photo_omitted) {
    body.photo_omitted = true;
  }
  return body;
}

async function retargetShift(db, previousUuid, openedUuid) {
  if (!previousUuid || !openedUuid || String(previousUuid) === String(openedUuid)) return;
  rememberOpenShift(String(openedUuid));
  const rows = await db.list();
  for (const row of rows) {
    if (row.action !== 'out') continue;
    if (String(row.shift_client_uuid || '') !== String(previousUuid)) continue;
    if (row.status === 'synced') continue;
    row.shift_client_uuid = String(openedUuid);
    await db.put(stripSecrets(row));
  }
}

export async function flushPunchQueue(api, { store, punchPhotoOn = false, userId, kioskCredentials } = {}) {
  const db = storeOrDefault(store);
  const activeUser = userId || readActiveUser();
  const now = Date.now();
  const pending = (await db.list())
    .filter((entry) => needsFlush(entry, now, punchPhotoOn))
    .filter((entry) => belongsToFlush(entry, activeUser))
    .sort((a, b) => tappedMillis(a.tapped_at) - tappedMillis(b.tapped_at));
  let synced = 0;
  let failed = 0;
  for (const queued of pending) {
    const entry = (await db.get(queued.client_uuid)) || queued;
    if (!needsFlush(entry, Date.now(), punchPhotoOn)) continue;
    if (earlierSwitchUnsynced(await db.list(), entry)) continue;
    entry.status = entry.status === 'synced' ? 'synced' : 'syncing';
    entry.attempts += 1;
    await db.put(stripSecrets(entry));
    const isSwitch = entry.action === 'switch' && entry.channel !== 'kiosk';
    const path = entry.channel === 'kiosk'
      ? '/attendance/offline/kiosk-punches/'
      : isSwitch
        ? '/attendance/time-clock/me/change-work/'
        : '/attendance/offline/punches/';
    try {
      const body = isSwitch
        ? buildSwitchSyncBody(entry, { punchPhotoOn })
        : buildSyncBody(entry, { punchPhotoOn });
      if (entry.channel === 'kiosk') {
        if (!kioskCredentials || !kioskCredentials.deviceSecret) {
          entry.status = 'pending';
          entry.attempts -= 1;
          await db.put(stripSecrets(entry));
          continue;
        }
        if (kioskCredentials.pin) body.pin = kioskCredentials.pin;
        if (kioskCredentials.kioskId) body.kiosk_id = kioskCredentials.kioskId;
      }
      const headers = {};
      if (entry.channel === 'kiosk') {
        headers['X-Device-Secret'] = kioskCredentials.deviceSecret;
        headers['X-Device-ID'] = kioskCredentials.kioskId || entry.kiosk_id || '';
      }
      const response = await api.post(path, body, { headers });
      entry.status = 'synced';
      entry.last_error = null;
      entry.next_retry_at = null;
      if (isSwitch && response?.data?.opened_client_uuid) {
        await retargetShift(db, entry.open_client_uuid, response.data.opened_client_uuid);
      }
      const photoStatus = response?.data?.photo_status;
      const sentPhoto = Boolean(body.photo || body.photo_jpeg);
      if (sentPhoto) {
        entry.photo = null;
        entry.photo_status = PHOTO_STATUS_SET.has(photoStatus) ? photoStatus : 'missing';
      } else if (!entry.photo) {
        entry.photo_status = 'missing';
      } else {
        entry.photo_status = 'held';
      }
      synced += 1;
    } catch (err) {
      const retry = isRetryable(err);
      entry.status = retry ? 'pending' : 'rejected';
      entry.last_error = err?.response?.data?.message || err?.message || 'unknown sync error';
      entry.next_retry_at = retry
        ? new Date(Date.now() + nextBackoffMs(entry.attempts)).toISOString()
        : null;
      failed += 1;
    }
    await db.put(stripSecrets(entry));
    notify();
  }
  return { synced, failed, total: pending.length };
}

export async function pendingPunchCount(store) {
  const rows = await storeOrDefault(store).list();
  return rows.filter((entry) => entry.status === 'pending' || entry.status === 'syncing').length;
}

export async function rememberRoster(rows, store) {
  const clean = (rows || []).map(rosterRow).filter((row) => row.badge_number);
  await storeOrDefault(store).replaceRoster(clean);
  return clean;
}

export async function lookupCachedBadge(badgeNumber, store) {
  const wanted = (badgeNumber || '').trim();
  if (!wanted) return null;
  const rows = await storeOrDefault(store).listRoster();
  const match = rows.find((row) => row.badge_number === wanted);
  if (!match) return null;
  return {
    employeeId: match.employee_id,
    displayName: `${match.first_name} ${match.last_name}`.trim(),
    badgeNumber: match.badge_number,
  };
}

export async function clearPunchQueueForUser(userId, store) {
  // Logout drops punches that already reached the server. Pending, syncing,
  // and rejected punches stay bound to this user and sync on the next login.
  const db = storeOrDefault(store);
  const rows = await db.list();
  let removed = 0;
  for (const entry of rows) {
    if (entry.channel === 'kiosk') continue;
    if (!userId || String(entry.user_id) !== String(userId)) continue;
    const photoHeld = entry.photo && entry.photo_status === 'held';
    if (entry.status === 'synced' && !photoHeld) {
      await db.remove(entry.client_uuid);
      removed += 1;
    }
  }
  clearOpenShift();
  bindActiveUser(null);
  if (removed) notify();
  return removed;
}

export async function syncPunchesForUser(api, userId, options = {}) {
  if (userId == null || userId === '') return { synced: 0, failed: 0, total: 0 };
  bindActiveUser(userId);
  return flushPunchQueue(api, { ...options, userId });
}

export async function listAttentionPunches(store) {
  const rows = await storeOrDefault(store).list();
  return rows
    .filter((entry) => isRejected(entry))
    .sort((a, b) => tappedMillis(a.tapped_at) - tappedMillis(b.tapped_at));
}

export async function acknowledgePunch(clientUuid, store) {
  const db = storeOrDefault(store);
  const entry = await db.get(clientUuid);
  if (!entry || !isRejected(entry)) return false;
  await db.remove(clientUuid);
  notify();
  return true;
}

export async function clearSyncedPunches(store) {
  const db = storeOrDefault(store);
  const rows = await db.list();
  let removed = 0;
  for (const entry of rows) {
    const photoHeld = entry.photo && entry.photo_status === 'held';
    if (entry.status === 'synced' && !photoHeld) {
      await db.remove(entry.client_uuid);
      removed += 1;
    }
  }
  if (removed) notify();
  return removed;
}
